import type { UseFormReturn } from 'react-hook-form';
import type { EditBankAddressFormValues } from './types';
import { useTranslation } from 'react-i18next';
import { useUpdateBankAddress, useDeleteBankAddress } from '../api';
import { useToast } from '@/shared/lib';
import { useCallback } from 'react';
import type { ConfirmationConfig } from '@/widgets';

type Props = {
  form: UseFormReturn<EditBankAddressFormValues>;
  templateId: string;
  onSuccess: () => void;
  openConfirmationModal: (config: ConfirmationConfig) => void;
};

type Params = {
  updateAddress: () => Promise<void>;
  deleteAddress: () => Promise<void>;
  isPending: boolean;
};

export const useEditBankAddressFormLogic = ({
  form,
  templateId,
  onSuccess,
  openConfirmationModal,
}: Props): Params => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { mutateAsync: updateBankAddress, isPending } = useUpdateBankAddress({
    onSuccess: () => {
      toast.success(t('savedAddresses.bankAddress.editing.result.success'));
      onSuccess();
    },
    onError: error => {
      toast.error(
        error.message || t('savedAddresses.bankAddress.editing.result.error')
      );
    },
  });

  const { mutateAsync: deleteBankAddress } = useDeleteBankAddress({
    onSuccess: () => {
      toast.success(t('savedAddresses.bankAddress.deletion.result.success'));
      onSuccess();
    },
    onError: error => {
      toast.error(
        error.message || t('savedAddresses.bankAddress.deletion.result.error')
      );
    },
  });

  const updateAddress = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    await updateBankAddress({
      templateId,
      data: {
        templateName: form.getValues('templateName'),
      },
    });
  }, [form, templateId, updateBankAddress]);

  const deleteAddress = useCallback(async () => {
    openConfirmationModal({
      title: t('savedAddresses.bankAddress.deletion.title'),
      description: t('savedAddresses.bankAddress.deletion.description'),
      confirmText: t('savedAddresses.bankAddress.deletion.confirmText'),
      cancelText: t('savedAddresses.bankAddress.deletion.cancelText'),
      onConfirm: async () =>
        await deleteBankAddress({
          templateId,
        }),
    });
  }, [templateId, deleteBankAddress, openConfirmationModal, t]);

  return {
    updateAddress,
    deleteAddress,
    isPending,
  };
};
